import {
  SignatureData,
  Palette,
  derivePalette,
  escapeHtml,
  normalizeUrl,
  displayUrl,
  nostrUrl,
  trackableLink,
} from './signature';

interface GenerateOptions {
  /** Pre-rendered QR code as a data URL (PNG). */
  qrDataUrl?: string;
  /** Absolute origin of the deployed app, used for trackable redirect links. */
  origin?: string;
  /** Anonymous signature id used for per-signature click counts. */
  sigId?: string;
}

/** Wraps a destination in a trackable redirect when enabled for this render. */
type LinkWrapper = (destination: string, linkType: string) => string;

/** Identity wrapper (no tracking). */
const passthrough: LinkWrapper = (dest) => dest;

/**
 * Active link wrapper for the current synchronous render. Set at the top of
 * generateSignatureHtml() before templates run. Generation is synchronous so
 * this module-level state is safe.
 */
let wrap: LinkWrapper = passthrough;

const SANS =
  "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";
const SERIF = "Georgia, 'Times New Roman', Times, serif";

function link(href: string, text: string, color: string): string {
  return `<a href="${escapeHtml(href)}" style="color:${color};text-decoration:none;" target="_blank">${escapeHtml(
    text,
  )}</a>`;
}

function socialLinks(data: SignatureData, p: Palette, font: string): string {
  const items: string[] = [];
  const { social } = data;
  const add = (label: string, url: string, type: string) => {
    if (url) items.push(link(wrap(url, type), label, p.accent));
  };
  if (social.nostr) add('Nostr', nostrUrl(social.nostr), 'nostr');
  add('X', normalizeUrl(social.twitter || ''), 'twitter');
  add('GitHub', normalizeUrl(social.github || ''), 'github');
  add('LinkedIn', normalizeUrl(social.linkedin || ''), 'linkedin');
  add('Telegram', normalizeUrl(social.telegram || ''), 'telegram');
  add('Instagram', normalizeUrl(social.instagram || ''), 'instagram');
  if (items.length === 0) return '';
  return `<tr><td style="padding-top:6px;font-size:12px;font-family:${font};">${items.join(
    ` <span style="color:${p.faint};">&middot;</span> `,
  )}</td></tr>`;
}

function acceptBadge(): string {
  return `<span style="display:inline-block;background:#0AC18E;color:#ffffff;font-size:11px;font-weight:bold;padding:4px 10px;border-radius:12px;font-family:${SANS};white-space:nowrap;">&#10003; Accept Bitcoin Cash</span>`;
}

function poweredBadge(): string {
  return `<span style="display:inline-block;background:#e8f8f2;color:#0A9E76;font-size:11px;font-weight:bold;padding:4px 10px;border-radius:12px;font-family:${SANS};white-space:nowrap;">&#9889; Powered by Bitcoin Cash</span>`;
}

function badges(data: SignatureData): string {
  const b: string[] = [];
  if (data.showAcceptBadge) b.push(acceptBadge());
  if (data.showPoweredBadge) b.push(poweredBadge());
  if (b.length === 0) return '';
  return `<tr><td style="padding-top:10px;">${b
    .map((x) => `<span style="margin-right:6px;">${x}</span>`)
    .join('')}</td></tr>`;
}

function bchLine(data: SignatureData, p: Palette, font: string): string {
  if (!data.bchAddress.trim()) return '';
  const short = data.bchAddress.replace(/^bitcoincash:/i, '');
  return `<tr><td style="padding-top:6px;font-size:12px;line-height:16px;color:${p.faint};font-family:${font};word-break:break-all;">BCH: <span style="color:${p.accent};font-weight:600;">${escapeHtml(
    short,
  )}</span></td></tr>`;
}

function contactRows(
  data: SignatureData,
  p: Palette,
  font: string,
  withIcons = true,
): string {
  const rows: string[] = [];
  const row = (icon: string, valueHtml: string) =>
    `<tr><td style="padding:2px 0;font-size:13px;line-height:18px;color:${p.muted};font-family:${font};">${
      withIcons ? `${icon} ` : ''
    }${valueHtml}</td></tr>`;

  if (data.phone.trim())
    rows.push(
      row('&#9742;', link(`tel:${data.phone.replace(/\s+/g, '')}`, data.phone, p.muted)),
    );
  if (data.email.trim())
    rows.push(
      row('&#9993;', link(wrap(`mailto:${data.email}`, 'email'), data.email, p.accent)),
    );
  if (data.website.trim())
    rows.push(
      row(
        '&#127760;',
        link(wrap(normalizeUrl(data.website), 'website'), displayUrl(data.website), p.accent),
      ),
    );
  if (data.address.trim())
    rows.push(row('&#128205;', `<span style="color:${p.muted};">${escapeHtml(data.address)}</span>`));
  return rows.join('');
}

function logoImg(data: SignatureData, size: number): string {
  if (!data.logo.trim()) return '';
  return `<img src="${escapeHtml(
    normalizeUrl(data.logo),
  )}" alt="${escapeHtml(data.business || data.name)}" width="${size}" style="display:block;border-radius:8px;width:${size}px;height:auto;max-width:${size}px;" />`;
}

function qrImg(qrDataUrl: string | undefined, size: number): string {
  if (!qrDataUrl) return '';
  return `<img src="${qrDataUrl}" alt="Bitcoin Cash payment QR" width="${size}" height="${size}" style="display:block;border-radius:8px;width:${size}px;height:${size}px;background:#ffffff;padding:4px;" />`;
}

function nameBlock(
  data: SignatureData,
  p: Palette,
  font: string,
  opts: { nameSize?: number; center?: boolean } = {},
): string {
  const size = opts.nameSize ?? 17;
  const align = opts.center ? 'text-align:center;' : '';
  const name = `<div style="font-size:${size}px;font-weight:700;color:${p.text};font-family:${font};${align}">${escapeHtml(
    data.name,
  )}</div>`;
  const roleText = [data.title, data.business].filter(Boolean).map(escapeHtml).join(' &middot; ');
  const role = roleText
    ? `<div style="font-size:13px;color:${p.accent};font-weight:600;font-family:${font};${align}">${roleText}</div>`
    : '';
  const tagline = data.tagline
    ? `<div style="font-size:12px;color:${p.muted};font-style:italic;padding-top:2px;font-family:${font};${align}">${escapeHtml(
        data.tagline,
      )}</div>`
    : '';
  return `${name}${role}${tagline}`;
}

/* ---------------- Templates ---------------- */

function modernTemplate(data: SignatureData, p: Palette, qr: string): string {
  const logo = logoImg(data, 72);
  const qrCell =
    data.showBchQr && qr
      ? `<td style="padding-left:16px;border-left:1px solid ${p.border};" valign="top">${qrImg(
          qr,
          84,
        )}<div style="font-size:10px;color:${p.faint};text-align:center;padding-top:3px;font-family:${SANS};">Pay with BCH</div></td>`
      : '';

  return `<table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;background:${p.bg};"><tr>
${logo ? `<td valign="top" style="padding-right:16px;">${logo}</td>` : ''}
<td valign="top" style="border-left:3px solid ${p.accent};padding-left:16px;">
<table cellpadding="0" cellspacing="0" border="0">
<tr><td>${nameBlock(data, p, SANS)}</td></tr>
${contactRows(data, p, SANS)}
${socialLinks(data, p, SANS)}
${bchLine(data, p, SANS)}
${badges(data)}
</table>
</td>
${qrCell}
</tr></table>`;
}

function classicTemplate(data: SignatureData, p: Palette, qr: string): string {
  return `<table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;background:${p.bg};font-family:${SANS};">
${data.logo.trim() ? `<tr><td style="padding-bottom:8px;">${logoImg(data, 60)}</td></tr>` : ''}
<tr><td>${nameBlock(data, p, SANS, { nameSize: 16 })}</td></tr>
<tr><td style="padding-top:8px;"><table cellpadding="0" cellspacing="0" border="0">${contactRows(
    data,
    p,
    SANS,
  )}${socialLinks(data, p, SANS)}${bchLine(data, p, SANS)}${badges(data)}</table></td></tr>
${data.showBchQr && qr ? `<tr><td style="padding-top:10px;">${qrImg(qr, 76)}</td></tr>` : ''}
</table>`;
}

function compactTemplate(data: SignatureData, p: Palette): string {
  const parts: string[] = [];
  parts.push(`<span style="font-weight:700;color:${p.text};">${escapeHtml(data.name)}</span>`);
  const role = [data.title, data.business].filter(Boolean).map(escapeHtml).join(', ');
  if (role) parts.push(`<span style="color:${p.muted};">${role}</span>`);
  const contacts: string[] = [];
  if (data.phone.trim())
    contacts.push(link(`tel:${data.phone.replace(/\s+/g, '')}`, data.phone, p.muted));
  if (data.email.trim())
    contacts.push(link(wrap(`mailto:${data.email}`, 'email'), data.email, p.accent));
  if (data.website.trim())
    contacts.push(
      link(wrap(normalizeUrl(data.website), 'website'), displayUrl(data.website), p.accent),
    );

  return `<table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;background:${p.bg};font-family:${SANS};font-size:13px;line-height:18px;">
<tr><td style="border-left:3px solid ${p.accent};padding:2px 0 2px 10px;">
<div>${parts.join(` <span style="color:${p.faint};">|</span> `)}</div>
${contacts.length ? `<div style="padding-top:3px;color:${p.muted};">${contacts.join(` <span style="color:${p.faint};">&middot;</span> `)}</div>` : ''}
${
  data.bchAddress.trim()
    ? `<div style="padding-top:3px;font-size:11px;color:${p.faint};">BCH: <span style="color:${p.accent};">${escapeHtml(
        data.bchAddress.replace(/^bitcoincash:/i, '').slice(0, 22),
      )}&hellip;</span></div>`
    : ''
}
<table cellpadding="0" cellspacing="0" border="0">${badges(data)}</table>
</td></tr>
</table>`;
}

function cardTemplate(data: SignatureData, p: Palette, qr: string): string {
  const qrCell =
    data.showBchQr && qr
      ? `<td valign="top" align="center" style="padding-left:18px;">${qrImg(
          qr,
          96,
        )}<div style="font-size:10px;color:${p.faint};padding-top:4px;font-family:${SANS};">Scan to pay in BCH</div></td>`
      : '';

  return `<table cellpadding="0" cellspacing="0" border="0" style="border-collapse:separate;border:1px solid ${p.border};border-radius:12px;background:${p.bg};">
<tr><td style="padding:18px 20px;">
<table cellpadding="0" cellspacing="0" border="0"><tr>
<td valign="top">
${data.logo.trim() ? `<div style="padding-bottom:10px;">${logoImg(data, 56)}</div>` : ''}
${nameBlock(data, p, SANS, { nameSize: 18 })}
<table cellpadding="0" cellspacing="0" border="0" style="padding-top:8px;">${contactRows(
    data,
    p,
    SANS,
  )}${socialLinks(data, p, SANS)}${bchLine(data, p, SANS)}${badges(data)}</table>
</td>
${qrCell}
</tr></table>
</td></tr>
</table>`;
}

function bannerTemplate(data: SignatureData, p: Palette, qr: string): string {
  const header = `<tr><td style="background:${p.accent};padding:12px 18px;border-radius:10px 10px 0 0;">
<table cellpadding="0" cellspacing="0" border="0" width="100%"><tr>
${data.logo.trim() ? `<td valign="middle" style="padding-right:12px;" width="1">${logoImg(data, 40)}</td>` : ''}
<td valign="middle">
<div style="font-size:17px;font-weight:700;color:${p.onAccent};font-family:${SANS};">${escapeHtml(
    data.name,
  )}</div>
${
  [data.title, data.business].filter(Boolean).length
    ? `<div style="font-size:12px;color:${p.onAccent};opacity:0.9;font-family:${SANS};">${[
        data.title,
        data.business,
      ]
        .filter(Boolean)
        .map(escapeHtml)
        .join(' &middot; ')}</div>`
    : ''
}
</td>
</tr></table>
</td></tr>`;

  const body = `<tr><td style="padding:14px 18px;background:${p.bg};border:1px solid ${p.border};border-top:none;border-radius:0 0 10px 10px;">
<table cellpadding="0" cellspacing="0" border="0" width="100%"><tr>
<td valign="top">
${data.tagline ? `<div style="font-size:12px;color:${p.muted};font-style:italic;padding-bottom:6px;font-family:${SANS};">${escapeHtml(data.tagline)}</div>` : ''}
<table cellpadding="0" cellspacing="0" border="0">${contactRows(data, p, SANS)}${socialLinks(
    data,
    p,
    SANS,
  )}${bchLine(data, p, SANS)}${badges(data)}</table>
</td>
${
  data.showBchQr && qr
    ? `<td valign="top" align="right" width="1" style="padding-left:16px;">${qrImg(qr, 84)}</td>`
    : ''
}
</tr></table>
</td></tr>`;

  return `<table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;max-width:520px;">${header}${body}</table>`;
}

function elegantTemplate(data: SignatureData, p: Palette, qr: string): string {
  return `<table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;background:${p.bg};font-family:${SERIF};">
${data.logo.trim() ? `<tr><td align="center" style="padding-bottom:8px;">${logoImg(data, 56)}</td></tr>` : ''}
<tr><td align="center" style="padding-bottom:6px;">${nameBlock(data, p, SERIF, {
    nameSize: 20,
    center: true,
  })}</td></tr>
<tr><td align="center"><div style="width:40px;border-top:2px solid ${p.accent};margin:4px auto 8px;"></div></td></tr>
<tr><td align="center"><table cellpadding="0" cellspacing="0" border="0" style="margin:0 auto;">${contactRows(
    data,
    p,
    SERIF,
    false,
  )}</table></td></tr>
<tr><td align="center"><table cellpadding="0" cellspacing="0" border="0" style="margin:0 auto;">${socialLinks(
    data,
    p,
    SERIF,
  )}${bchLine(data, p, SERIF)}</table></td></tr>
${
  data.showBchQr && qr
    ? `<tr><td align="center" style="padding-top:10px;">${qrImg(qr, 80)}</td></tr>`
    : ''
}
<tr><td align="center"><table cellpadding="0" cellspacing="0" border="0" style="margin:0 auto;">${badges(
    data,
  )}</table></td></tr>
</table>`;
}

function stackedTemplate(data: SignatureData, p: Palette, qr: string): string {
  const panel = `<td valign="top" width="6" style="background:${p.accent};border-radius:8px 0 0 8px;">&nbsp;</td>
<td valign="top" width="150" style="background:${p.panel};padding:16px;border-radius:0;">
${data.logo.trim() ? `<div style="padding-bottom:10px;">${logoImg(data, 60)}</div>` : ''}
<div style="font-size:16px;font-weight:700;color:${p.text};font-family:${SANS};">${escapeHtml(
    data.name,
  )}</div>
${
  [data.title, data.business].filter(Boolean).length
    ? `<div style="font-size:12px;color:${p.accent};font-weight:600;font-family:${SANS};padding-top:2px;">${[
        data.title,
        data.business,
      ]
        .filter(Boolean)
        .map(escapeHtml)
        .join('<br/>')}</div>`
    : ''
}
${
  data.showBchQr && qr
    ? `<div style="padding-top:10px;">${qrImg(qr, 72)}</div>`
    : ''
}
</td>`;

  const body = `<td valign="top" style="padding:16px;background:${p.bg};">
${data.tagline ? `<div style="font-size:12px;color:${p.muted};font-style:italic;padding-bottom:6px;font-family:${SANS};">${escapeHtml(data.tagline)}</div>` : ''}
<table cellpadding="0" cellspacing="0" border="0">${contactRows(data, p, SANS)}${socialLinks(
    data,
    p,
    SANS,
  )}${bchLine(data, p, SANS)}${badges(data)}</table>
</td>`;

  return `<table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;border:1px solid ${p.border};border-radius:8px;overflow:hidden;">
<tr>${panel}${body}</tr></table>`;
}

function minimalTemplate(data: SignatureData, p: Palette): string {
  const role = [data.title, data.business].filter(Boolean).map(escapeHtml).join(', ');
  const contacts: string[] = [];
  if (data.email.trim())
    contacts.push(link(wrap(`mailto:${data.email}`, 'email'), data.email, p.accent));
  if (data.phone.trim())
    contacts.push(link(`tel:${data.phone.replace(/\s+/g, '')}`, data.phone, p.muted));
  if (data.website.trim())
    contacts.push(
      link(wrap(normalizeUrl(data.website), 'website'), displayUrl(data.website), p.accent),
    );

  return `<table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;background:${p.bg};font-family:${SANS};font-size:13px;line-height:19px;">
<tr><td>
<div style="font-weight:700;color:${p.text};">${escapeHtml(data.name)}</div>
${role ? `<div style="color:${p.muted};">${role}</div>` : ''}
${data.tagline ? `<div style="color:${p.faint};font-style:italic;padding-top:2px;">${escapeHtml(data.tagline)}</div>` : ''}
${contacts.length ? `<div style="padding-top:6px;color:${p.muted};">${contacts.join(` <span style="color:${p.faint};">&middot;</span> `)}</div>` : ''}
${
  data.bchAddress.trim()
    ? `<div style="padding-top:2px;font-size:12px;color:${p.faint};word-break:break-all;">BCH: <span style="color:${p.accent};">${escapeHtml(
        data.bchAddress.replace(/^bitcoincash:/i, ''),
      )}</span></div>`
    : ''
}
<table cellpadding="0" cellspacing="0" border="0">${badges(data)}</table>
</td></tr>
</table>`;
}

export function generateSignatureHtml(
  data: SignatureData,
  options: GenerateOptions = {},
): string {
  const accent = data.accentColor || '#0AC18E';
  const bg = data.bgColor || '#ffffff';
  const text = data.textColor || '#111111';
  const p = derivePalette(bg, text, accent);
  const qr = options.qrDataUrl || '';

  // Configure link tracking for this render (opt-in + needs an absolute origin).
  wrap =
    data.trackLinks && options.origin
      ? (dest, type) =>
          trackableLink(dest, type, options.origin as string, true, options.sigId)
      : passthrough;

  let inner: string;
  switch (data.template) {
    case 'classic':
      inner = classicTemplate(data, p, qr);
      break;
    case 'compact':
      inner = compactTemplate(data, p);
      break;
    case 'card':
      inner = cardTemplate(data, p, qr);
      break;
    case 'banner':
      inner = bannerTemplate(data, p, qr);
      break;
    case 'elegant':
      inner = elegantTemplate(data, p, qr);
      break;
    case 'stacked':
      inner = stackedTemplate(data, p, qr);
      break;
    case 'minimal':
      inner = minimalTemplate(data, p);
      break;
    case 'modern':
    default:
      inner = modernTemplate(data, p, qr);
      break;
  }
  wrap = passthrough; // reset shared state
  return `<div style="font-family:${SANS};color:${p.text};background:${p.bg};display:inline-block;">${inner}</div>`;
}
